import { useCallback, useEffect, useRef, useState } from "react"
import { motion } from "framer-motion"
import * as actions from '../redux/actions/billing';
import {
    ArrowLeft,
    TrendingUp,
    Users,
    BarChart3,
    Mic,
    Megaphone,
    Headphones,
    Brain,
    GraduationCap,
    Play,
    CheckCircle,
} from "lucide-react"
import PersonMoney from '../assets/images/icons/person-money.svg'
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { PricingPlanModal } from "../components/PricingPlanModal";
import { DistributionModal } from "../components/DistributionAccess/DistributionModal";
import { generateMetadata } from "../seo/metadataGenerator";
import { generateBreadcrumbSchema, generateGraphSchema, generateSoftwareApplicationSchema, generateWebPageSchema } from "../seo/schemaGenerator";
import SeoHead from "../seo/SeoHead";
import { SITE_URL, PAGE_OG_IMAGES } from "../seo/constants";
import {
    FEATURE_MODULE_SLUGS,
    getFeatureHashPath,
    getFeatureIndexBySlug,
    getFeatureSlugByIndex,
} from "../seo/featureModules";
import { usePrerenderReady } from "../hooks/usePrerenderReady";

export default function FeaturesPage() {
    usePrerenderReady();
    const dispatch = useDispatch()
    const navigate = useNavigate()
    const location = useLocation()
    const { id } = useParams();
    const [showDistributionModal, setShowDistributionModal] = useState(false);
    const [cursorPosition, setCursorPosition] = useState({ x: -100, y: -100 });
    const [isHovering, setIsHovering] = useState(false);
    const [isLoad, setIsLoad] = useState(false)
    const [showPricingPlanModal, setShowPricingPlanModal] = useState(false);
    const [activeFeature, setActiveFeature] = useState(() => {
        const fromHash = getFeatureIndexBySlug(window.location.hash);
        if (fromHash >= 0) {
            return fromHash;
        }
        const parsedId = Number.parseInt(window.location.pathname.split("/").pop(), 10);
        return Number.isInteger(parsedId) && parsedId >= 0 && parsedId < FEATURE_MODULE_SLUGS.length
            ? parsedId
            : 0;
    })
    // Updated to match new state structure
    const { subscriptionPlan, hasActiveSubscription } = useSelector((state) => state.billing);
    const token = localStorage.getItem("authToken");

    // Extract nested data safely
    const planList = subscriptionPlan || [];
    const monthlyPlan = planList.find(item => item.interval === "monthly") || null;
    const lastSubscriptionFetched = subscriptionPlan?.lastFetched;

    const updateCursorPosition = useCallback((e) => {
        requestAnimationFrame(() => {
            setCursorPosition({ x: e.clientX, y: e.clientY });
        });
    }, []);

    useEffect(() => {
        document.body.style.cursor = 'none';
        const handleMouseEnter = () => setIsHovering(true);
        const handleMouseLeave = () => setIsHovering(false);

        const interactiveElements = document.querySelectorAll(
            'a, button, input, [role="button"]'
        );
        interactiveElements.forEach((element) => {
            element.addEventListener("mouseenter", handleMouseEnter);
            element.addEventListener("mouseleave", handleMouseLeave);
        });

        window.addEventListener("mousemove", updateCursorPosition);

        // Simulate loading time (remove this in production and use real loading logic)
        const timer = setTimeout(() => {
            setIsLoad(false);
        }, 3000);

        return () => {
            clearTimeout(timer);
            document.body.style.cursor = 'auto';
            window.removeEventListener("mousemove", updateCursorPosition);
            interactiveElements.forEach((element) => {
                element.removeEventListener("mouseenter", handleMouseEnter);
                element.removeEventListener("mouseleave", handleMouseLeave);
            });
        };
    }, [updateCursorPosition]);

    useEffect(() => {
        if (id == null) {
            return;
        }
        const parsedId = Number.parseInt(id, 10);
        const slug = Number.isInteger(parsedId) && parsedId >= 0
            ? getFeatureSlugByIndex(parsedId)
            : getFeatureIndexBySlug(id) >= 0
                ? id
                : null;
        navigate(getFeatureHashPath(slug ?? 0), { replace: true });
    }, [id, navigate]);

    useEffect(() => {
        if (id != null) {
            return;
        }
        const hashIndex = getFeatureIndexBySlug(location.hash);
        setActiveFeature(hashIndex >= 0 ? hashIndex : 0);
    }, [id, location.hash]);

    const selectFeature = useCallback((index) => {
        setActiveFeature(index);
        const nextPath = getFeatureHashPath(index);
        if (`${location.pathname}${location.hash}` !== nextPath) {
            navigate(nextPath, { replace: true });
        }
    }, [location.hash, location.pathname, navigate]);

    const hasFetched = useRef(false);
    useEffect(() => {
        const fetchPlans = async () => {
            const shouldFetch =
                !planList?.length ||
                !lastSubscriptionFetched ||
                Date.now() - lastSubscriptionFetched > 1000 * 60 * 30; // 30 mins cache

            if (shouldFetch && !hasFetched.current) {
                hasFetched.current = true; // prevent duplicate fetches
                await dispatch(actions.getSubscriptionPlans());
            }
        };

        fetchPlans();

        // reset flag when component unmounts or on logout, if needed
        return () => {
            hasFetched.current = false;
        };
    }, [dispatch, planList?.length, lastSubscriptionFetched]);

    const handleSubscription = async () => {
        if (token) {
            try {
                setIsLoad(true); // 🔹 start loading
                if (hasActiveSubscription) {
                    await dispatch(actions.getCustomerPortalSession());
                } else {
                    setShowPricingPlanModal(true);
                }
            } catch (err) {
                console.error('Error handling Pro button:', err);
            } finally {
                setIsLoad(false); // 🔹 stop loading
            }
        } else {
            navigate('/signup')
        }
    }

    const features = [
        {
            slug: FEATURE_MODULE_SLUGS[0],
            icon: <TrendingUp className="w-8 h-8 text-[#FA6138]" />,
            title: "Growth Engine",
            subtitle: "Automated Ads & Campaigns",
            description:
                "Launch fully-automated campaigns across Meta, TikTok, YouTube, and more — all optimized for music, events, merch, and beyond.",
            value: "Replaces $3K/month agencies",
            details: [
                "🎯 Auto-generate landing pages for your songs, releases, playlists, events & more",
                "🧭 Goal-specific setups for everything from presaves to merch and video views",
                "🌍 Smart budget allocation with country-specific control (assign % by region)",
                "🧠 AI-powered audience targeting based on your real fan data",
                "📈 Performance tracking with cost breakdowns and campaign health insights",
                "🔁 Continuous optimization based on real-time conversion signals",
                "✏️ Full control to edit campaign budgets, targeting, dates, and times",
                "🚀 One-click ad creation across Meta, TikTok, YouTube — promote any DSP or destination"
            ],
            bgGradient: "from-[#312e81]/20 via-[#1e1b4b]/15 to-[#0f0f23]/25",
            borderColor: "border-[#6366f1]/25",
        },
        {
            slug: FEATURE_MODULE_SLUGS[1],
            icon: <img src={PersonMoney} className="w-8 h-8 text-[#FA6138]" />,
            title: "Artist Manager",
            subtitle: "Smart Links, Fanbase, Revenue",
            description:
                "Manage the things that count — the ones you never got around to. Track fans, manage smart links, and monitor revenue + expenses.",
            value: "Replaces $2K/month management",
            details: [
                "💸 Revenue tracking across DSPs, stores, merch and more",
                "🔗 Smart link creation for releases, presaves, playlists, and more",
                "📆 Release planning with calendars, alerts, and milestone tracking",
                "🧾 Expense tracking to stay on top of costs and payouts",
                "🎯 Performance insights to monitor career growth across the board",
                "🤝 Tracking for contracts, splits, and task assignments",
                "🧠 Fan segmentation to build real relationships, not just reach"
            ],
            bgGradient: "from-[#1e1b4b]/20 via-[#312e81]/15 to-[#4338ca]/10",
            borderColor: "border-[#4f46e5]/20",
        },
        {
            slug: FEATURE_MODULE_SLUGS[2],
            icon: <BarChart3 className="w-8 h-8 text-[#FA6138]" />,
            title: "Data Analyst",
            subtitle: "Real-Time Music Insights",
            description:
                "See what’s working. Scale what’s next. Real-time performance insights across all DSPs, audiences, playlists, and platforms.",
            value: "Replaces $500/month tools",
            details: [
                "📈 Real-time streaming analytics across total streams, listeners, followers, interactions, and playlist adds",
                "🧠 Audience intelligence with top cities, countries, genders, and growth markets visualized on a live heat map",
                "🔥 Top-performing content highlights for songs, videos, playlists, and most-milestoned tracks in one view",
                "🎧 Playlist + chart tracking showing every playlist add, curator info, follower counts, and daily chart positions",
                "📻 Radio play data showing FM/AM airplay globally with timeline and frequency mapping",
                "📊 Popularity scoring by song with proprietary metrics to show which tracks are breaking through",
                "🎯 Conversion-ready insights from Amplitude AI to take action on top tracks, trends, or campaigns instantly",
                "📋 Milestone & catalog tracking that auto-logs major achievements and ties them to song or artist growth"
            ],
            bgGradient: "from-[#4338ca]/15 via-[#3730a3]/12 to-[#312e81]/18",
            borderColor: "border-[#818cf8]/20",
        },
        {
            slug: FEATURE_MODULE_SLUGS[3],
            icon: <Mic className="w-8 h-8 text-[#FA6138]" />,
            title: "Content Creator",
            subtitle: "Social Media Automation and Content Creation",
            description:
                "Generate and schedule content across social platforms. AI-powered content creation and automated posting.",
            value: "Replaces $1K/month creators",
            details: [
                "🎨 AI-generated social media content for Instagram, TikTok, YouTube, and more",
                "🖼️ Visual content creation tools to design posts, stories, and carousels",
                "🔁 Cross-platform content adaptation to resize, reformat, and reuse effortlessly",
                "🗓️ Automated posting schedules to maintain consistency and save time",
                "#️⃣ Hashtag optimization to boost discovery and engagement",
                "📊 Engagement tracking and optimization to refine your content strategy",
            ],
            bgGradient: "from-[#1e3a8a]/25 via-[#1e1b4b]/20 to-[#0f0f23]/30",
            borderColor: "border-[#c7d2fe]/15",
        },
        {
            slug: FEATURE_MODULE_SLUGS[4],
            icon: <Megaphone className="w-8 h-8 text-[#FA6138]" />,
            title: "Publicist",
            subtitle: "Playlists, Sync & Press Automation",
            description: "Automate playlist pitching, press coverage, sync deals, and label outreach — all in one place. Connect with curators, editors, blogs, and music supervisors at scale.",
            value: "Replaces $1.5K/month PR",
            details: [
                "📝 Playlist pitching to editors, tastemakers, and curators across DSPs",
                "📰 Press outreach automation with contact lists, tags, and tracking",
                "🧾 Press kit creation with auto-generated bios, images, and links",
                "📢 Press release creation and distribution across media outlets",
                "💼 Sync deal pipeline management with status tracking and notes",
                "🎬 Music supervisor targeting by region, genre, and project type",
                "🏷️ Label opportunity tracking with status tags and performance tie-ins",
                "📈 Coverage tracking for playlists, blogs, press hits, and sync placements"
            ],
            bgGradient: "from-[#6366f1]/20 via-[#4f46e5]/15 to-[#4338ca]/20",
            borderColor: "border-[#a5b4fc]/20",
        },
        {
            slug: FEATURE_MODULE_SLUGS[5],
            icon: <Headphones className="w-8 h-8 text-[#FA6138]" />,
            title: "UrStudio",
            subtitle: "AI Mastering & Audio Tools",
            description:
                "Your complete AI-powered post-production suite — from mixing to mastering, built to take your track from rough to release-ready in minutes.",
            value: "Covers your entire post-production workflow saving your thousands of dollars.",
            details: [
                {
                    title: "🎚️ Mix Enhancer",
                    description: "Instantly improve balance, clarity, and overall sound quality"
                },
                {
                    title: "🎛️ AI Mixing Engineer",
                    description: "Automated mixing tailored to your genre and style"
                },
                {
                    title: "🔊 AI Mastering Engineer",
                    description: "Studio-quality mastering for loudness, polish, and release readiness"
                },
                {
                    title: "🧠 Mix Analyst",
                    description: "Get real-time feedback and actionable suggestions to improve your track"
                },
                {
                    title: "🎧 Unlimited Real-Time Previews",
                    description: "Hear your track instantly and tweak it as much as you want — no limits, no guesswork"
                },
                {
                    title: "✂️ Stem Processing & Splitting",
                    description: "Isolate vocals, drums, and instruments for full control and editing"
                },
                {
                    title: "📦 Export & Release-Ready Formats",
                    description: "High-quality WAV, MP3, and platform-ready outputs"
                },
                {
                    title: "🗂️ Dedicated Audio Libraries",
                    description: "Organize and access your mixes, masters, stems, and versions in one place"
                },
            ],
            bgGradient: "from-[#0f0f23]/30 via-[#1e1b4b]/25 to-[#312e81]/20",
            borderColor: "border-[#4f46e5]/25",
        },
        {
            slug: FEATURE_MODULE_SLUGS[6],
            icon: <Brain className="w-8 h-8 text-[#FA6138]" />,
            title: "Amplitude AI",
            subtitle: "Your Strategy Brain",
            description:
                "The brain behind UpNComer. AI trained on your catalog, campaigns, and fans — powering the next best action across every module.",
            value: "Replaces entire teams",
            details: [
                {
                    title: "🔮 Predictive analytics for your next best move",
                    description: "Trained on your music, audience, and campaign history — updated in real time."
                },
                {
                    title: "🌍 Global trend detection before they peak",
                    description: "Surface emerging sounds, regions, and audience shifts before they go mainstream."
                },
                {
                    title: "🧠 Fan behavior analysis across every platform",
                    description: "Map who’s listening, where they’re converting, and how to re-engage them."
                },
                {
                    title: "📈 Campaign forecast engine",
                    description: "See ROI projections before you launch — adjust budgets, platforms, and targets instantly."
                },
                {
                    title: "⚙️ End-to-end automation built into every tool",
                    description: "Let Amplitude optimize campaigns, schedules, and targeting without lifting a finger."
                },
                {
                    title: "🧩 Modular decision engine integrated across the platform",
                    description: "Powers Growth Engine, Artist Manager, Data Analyst, and more — with 1-click actions."
                },
                {
                    title: "📊 AI trained on 100M+ industry data points",
                    description: "Your insights aren’t just personal — they’re global. Trends, genres, formats, regions."
                },
                {
                    title: "🎯 Long-term strategy builder, while you focus on your craft",
                    description: "Let Amplitude connect short-term wins to big-picture momentum."
                },
            ],
            bgGradient: "from-[#3730a3]/20 via-[#4338ca]/15 to-[#6366f1]/18",
            borderColor: "border-[#818cf8]/25",
        },
        {
            slug: FEATURE_MODULE_SLUGS[7],
            icon: <GraduationCap className="w-8 h-8 text-[#FA6138]" />,
            title: "ProfessorU",
            subtitle: "Learn While You Build",
            description:
                "Built-in education that costs nothing, saves time, and gives you more than it ever asks. Learn while you build. Grow while you create.",
            value: "Replaces $300+ per course and never charges a cent.",
            details: [
                {
                    title: "🎥 Live and on-demand expert masterclasses",
                    description: "From charting artists to music execs — hear directly from those shaping the industry.",
                },
                {
                    title: "📚 Interactive, always-updated industry playbooks",
                    description: "Step-by-step guides to mastering every module inside UpNComer.",
                },
                {
                    title: "🧠 Personalized learning paths based on your goals",
                    description: "Releasing an album? Growing a fanbase? Monetizing your merch? We build your track.",
                },
                {
                    title: "🛠️ Skill-building woven into every tool",
                    description: "Learn how to run smarter campaigns, get more out of your data, and master your audio — without leaving your flow.",
                },
                {
                    title: "🌍 Real-world trend breakdowns and case studies",
                    description: "We decode the biggest wins and shifts across the global music landscape.",
                },
                {
                    title: "🎁 Rewards for career-building and PR",
                    description: "Focus on the outcomes you want, and let your rewards catalyse them with credits and free access to the platform.",
                },
                {
                    title: "🎙️ Podcast sessions with trailblazers and innovators",
                    description: "Hear how others broke through, and what they wish they’d known earlier.",
                },
                {
                    title: "💬 Peer learning and mentorship channels",
                    description: "You’re never alone — connect, ask, share, and grow with fellow artists.",
                },
            ],
            bgGradient: "from-[#312e81]/25 via-[#1e1b4b]/20 to-[#3730a3]/22",
            borderColor: "border-[#6366f1]/20",
        },
    ]

    //SEO
    const metadata = generateMetadata({
        title:
            "Your Music Team | UpNComer",

        description:
            "Discover AI-powered tools for music marketing, music campaigning, distribution, artist analytics, fan engagement, mixing, mastering, playlist pitching, content creation and career growth. UpNComer helps independent artists manage releases, grow audiences and scale music careers from one unified platform.",

        image: PAGE_OG_IMAGES.features,

        // Hash deep links (/features#growth-engine) are the same document.
        // Legacy /features/:id redirects to the hash; never self-canonicalize.
        path: "/features",
    })

    const webPageSchema = generateWebPageSchema({
        title:
            "Your Music Team | UpNComer",

        description:
            "Discover AI-powered tools for music marketing, music campaigning, distribution, artist analytics, fan engagement, mixing, mastering, playlist pitching, content creation and career growth. UpNComer helps independent artists manage releases, grow audiences and scale music careers from one unified platform.",

        url: `${SITE_URL}/features`,
    })

    const breadcrumbItems = [
        {
            "name": "Home",
            "path": "/"
        },
        {
            "name": "Features",
            "path": "/features"
        }
    ]

    const schemas = generateGraphSchema([
        generateSoftwareApplicationSchema(),
        webPageSchema,
        generateBreadcrumbSchema(breadcrumbItems)
    ])

    return (
        <>
            <SeoHead
                metadata={metadata}
                schemas={schemas}
            />
            <div className="relative min-h-screen bg-gradient-to-br from-[#000456] via-[#00067E] to-[#1e3a8a] overflow-hidden">
                <div
                    className={`cursor-spotlight ${isHovering ? "hovering" : ""}`}
                    style={{
                        left: `${cursorPosition.x}px`,
                        top: `${cursorPosition.y}px`,
                        transform: `translate(-50%, -50%)`,
                    }}
                >
                    <div className="inner-circle"></div>
                </div>

                {/* Enhanced Multi-layered Background */}
                <div className="absolute inset-0 z-0">
                    <div className="absolute inset-0 bg-gradient-to-br from-[#000322] via-[#00067E] via-[#0a1a8a] via-[#1e3a8a] to-[#3b82f6] opacity-90" />

                    <motion.div
                        animate={{
                            scale: [1, 1.2, 1],
                            opacity: [0.3, 0.6, 0.3],
                        }}
                        transition={{
                            duration: 10,
                            repeat: Number.POSITIVE_INFINITY,
                            ease: "easeInOut",
                        }}
                        className="absolute top-20 right-20 w-96 h-96 bg-gradient-to-br from-[#6366f1]/20 via-[#4f46e5]/15 to-[#FA6138]/10 rounded-full blur-3xl"
                    />

                    <motion.div
                        animate={{
                            scale: [1, 1.3, 1],
                            opacity: [0.2, 0.5, 0.2],
                        }}
                        transition={{
                            duration: 12,
                            repeat: Number.POSITIVE_INFINITY,
                            ease: "easeInOut",
                            delay: 3,
                        }}
                        className="absolute bottom-32 left-16 w-72 h-72 bg-gradient-to-br from-[#FA6138]/12 via-[#818cf8]/15 to-[#6366f1]/18 rounded-full blur-3xl"
                    />
                </div>

                <main className="relative z-10 mt-16 md:mt-18 py-10 lg:py-16">
                    <div className="container">
                        {/* Header */}
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.8 }}
                            className="text-center mb-10 lg:mb-14"
                        >
                            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold bg-gradient-to-r from-[#c7d2fe] via-[#a5b4fc] to-[#818cf8] bg-clip-text text-transparent mb-6 drop-shadow-lg">
                                Your Music Team
                            </h1>
                            <p className="bg-gradient-to-r from-[#818cf8]/80 via-[#6366f1]/85 to-[#4f46e5]/80 bg-clip-text text-transparent text-lg lg:text-xl font-light max-w-3xl mx-auto leading-relaxed">
                                Replace expensive industry services with AI-powered automation. Everything you need to succeed as an
                                independent artist, all in one platform.
                            </p>
                        </motion.div>

                        {/* Interactive Features Grid */}
                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-20">
                            {/* Features List */}
                            <div className="lg:col-span-1 space-y-4 max-h-[50vh] lg:max-h-full overflow-auto">
                                {features.map((feature, index) => (
                                    <motion.div
                                        key={feature.slug}
                                        id={feature.slug}
                                        initial={{ opacity: 0, x: -20 }}
                                        animate={{ opacity: 1, x: 0 }}
                                        transition={{ duration: 0.5, delay: index * 0.1 }}
                                        className={`p-4 rounded-xl cursor-pointer transition-all duration-300 ${activeFeature === index
                                            ? `bg-gradient-to-br ${feature?.bgGradient} border ${feature?.borderColor} shadow-lg`
                                            : "bg-white/5 border border-white/10 hover:bg-white/10"
                                            }`}
                                        onClick={() => selectFeature(index)}
                                    >
                                        <div className="flex items-center space-x-4">
                                            <div className={`p-2 rounded-lg ${activeFeature === index ? "bg-[#FA6138]/20" : "bg-white/10"}`}>
                                                {feature?.icon}
                                            </div>
                                            <div>
                                                <h3 className="text-lg font-semibold text-white">{feature?.title}</h3>
                                                <p className="text-sm text-[#a5b4fc]/80">{feature?.subtitle}</p>
                                            </div>
                                        </div>
                                    </motion.div>
                                ))}
                            </div>

                            {/* Feature Details */}
                            <div className="lg:col-span-2">
                                <motion.div
                                    key={activeFeature}
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.5 }}
                                    className={`bg-gradient-to-br ${features[activeFeature]?.bgGradient} backdrop-blur-xl border ${features[activeFeature]?.borderColor} rounded-2xl p-4 lg:p-8 hh-full`}
                                    style={{
                                        boxShadow: "0 20px 40px rgba(99, 102, 241, 0.2), inset 0 1px 0 rgba(165, 180, 252, 0.1)",
                                    }}
                                >
                                    <div className="flex flex-col sm:flex-row items-start gap-x-6 gap-y-4 mb-8">
                                        <div className="p-4 bg-[#FA6138]/20 rounded-2xl">{features[activeFeature]?.icon}</div>
                                        <div>
                                            <h2 className="text-3xl font-bold bg-gradient-to-r from-[#c7d2fe] to-[#a5b4fc] bg-clip-text text-transparent mb-2">
                                                {features[activeFeature]?.title}
                                            </h2>
                                            <p className="text-[#818cf8]/80 text-lg mb-4 leading-relaxed">
                                                {features[activeFeature]?.description}
                                            </p>
                                            <div className="text-sm text-[#FA6138] font-medium tracking-wide">
                                                {features[activeFeature]?.value}
                                            </div>
                                        </div>
                                    </div>

                                    <div className="space-y-4">
                                        <h3 className="text-xl font-semibold text-[#a5b4fc] mb-4">Key Features:</h3>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-8">
                                            {features[activeFeature]?.details.includes("title") ?
                                                features[activeFeature]?.details.map((detail, index) => (
                                                    <motion.div
                                                        key={index}
                                                        initial={{ opacity: 0, x: -10 }}
                                                        animate={{ opacity: 1, x: 0 }}
                                                        transition={{ duration: 0.3, delay: index * 0.1 }}
                                                        className="flex items-start space-x-3"
                                                    >
                                                        <CheckCircle className="w-5 h-5 text-[#FA6138] flex-shrink-0 mt-0.5" />
                                                        <span className="text-[#c7d2fe]/90 text-sm">{detail.title}</span>
                                                    </motion.div>
                                                )) :
                                                features[activeFeature]?.details.map((detail, index) => (
                                                    <motion.div
                                                        key={index}
                                                        initial={{ opacity: 0, x: -10 }}
                                                        animate={{ opacity: 1, x: 0 }}
                                                        transition={{ duration: 0.3, delay: index * 0.1 }}
                                                        className="flex items-start space-x-3"
                                                    >
                                                        {typeof detail === "string" ?
                                                            <>
                                                                <CheckCircle className="w-5 h-5 text-[#FA6138] flex-shrink-0 mt-0.5" />
                                                                <span className="text-[#c7d2fe]/90 text-sm">{detail}</span>
                                                            </>
                                                            :
                                                            <div className="flex items-start gap-2">
                                                                <CheckCircle className="w-5 h-5 text-[#FA6138] flex-shrink-0 mt-0.5" />
                                                                <div>
                                                                    <span className="text-[#c7d2fe]/90 text-sm font-semibold">
                                                                        {detail.title}
                                                                    </span>
                                                                    <p className="text-[#c7d2fe]/70 text-sm font-normal mt-1">
                                                                        {detail.description}
                                                                    </p>
                                                                </div>
                                                            </div>
                                                        }
                                                    </motion.div>
                                                ))}
                                        </div>
                                    </div>
                                </motion.div>
                            </div>
                        </div>

                        {/* Value Proposition */}
                        <motion.div
                            initial={{ opacity: 0, y: 30 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.8, delay: 0.6 }}
                            className="bg-gradient-to-br from-[#312e81]/20 via-[#1e1b4b]/15 to-[#0f0f23]/25 backdrop-blur-xl border border-[#6366f1]/20 rounded-2xl p-4 lg:p-8 mb-14 xl:mb-20"
                            style={{
                                boxShadow: "0 20px 40px rgba(99, 102, 241, 0.25), inset 0 1px 0 rgba(165, 180, 252, 0.15)",
                            }}
                        >
                            <div className="text-center mb-12">
                                <h2 className="text-4xl font-bold bg-gradient-to-r from-[#c7d2fe] to-[#a5b4fc] bg-clip-text text-transparent mb-6">
                                    Replace a $10K/Month Team for ${monthlyPlan?.price}
                                </h2>
                                <p className="text-[#818cf8]/80 text-lg max-w-3xl mx-auto">
                                    Get the power of a full label team while keeping 100% creative and financial control.
                                </p>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                                <div className="text-center">
                                    <div className="text-4xl font-bold text-[#FA6138] mb-2">$10,000+</div>
                                    <div className="text-[#a5b4fc] mb-4">Traditional Monthly Cost</div>
                                    <div className="text-sm text-[#818cf8]/70">
                                        Marketing agency, manager, publicist, audio engineer, data analyst, content creator
                                    </div>
                                </div>
                                <div className="flex items-center justify-center">
                                    <ArrowLeft className="w-8 h-8 text-[#FA6138] transform rotate-180" />
                                </div>
                                <div className="text-center">
                                    <div className="text-4xl font-bold text-[#FA6138] mb-2">${monthlyPlan?.price}</div>
                                    <div className="text-[#a5b4fc] mb-4">UpNComer Pro</div>
                                    <div className="text-sm text-[#818cf8]/70">All features, unlimited usage, 100% royalty retention</div>
                                </div>
                            </div>
                        </motion.div>

                        {/* CTA Section */}
                        <motion.div
                            initial={{ opacity: 0, y: 30 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.8, delay: 0.8 }}
                            className="text-center"
                        >
                            <h2 className="text-3xl font-bold text-[#c7d2fe] mb-6">Ready to Build Your Music Empire?</h2>
                            <p className="text-[#818cf8]/80 mb-8 max-w-2xl mx-auto">
                                Join hundreds of independent artists who are already growing their careers with UpNComer.
                            </p>
                            <div className="flex flex-col sm:flex-row gap-4 justify-center">
                                <button
                                    onClick={handleSubscription}
                                    className="inline-block bg-gradient-to-r from-[#FA6138] to-[#e55532] text-white text-center
                         px-8 py-4 text-sm font-medium tracking-wider rounded-xl hover:from-[#e55532] hover:to-[#FA6138]
                         transition-all duration-300 shadow-xl hover:shadow-2xl transform hover:scale-105"
                                >
                                    BECOME AN UPNCOMER
                                </button>
                                <button
                                    onClick={() => setShowDistributionModal(true)}
                                    className="inline-block bg-white text-theme_clr2 text-center
                             px-5 py-4 text-sm font-medium tracking-wider rounded-xl hover:from-[#e55532] hover:to-[#FA6138]
                             transition-all duration-300 shadow-xl hover:shadow-2xl transform hover:scale-105
                             backdrop-blur-sm border border-white/10"
                                    style={{
                                        boxShadow: "0 10px 30px rgba(250, 97, 56, 0.3)",
                                    }}
                                >
                                    DISTRIBUTE YOUR MUSIC
                                </button>
                                <a
                                    href="https://www.instagram.com/upncomer.co/"
                                    className="inline-flex items-center justify-center bg-[#1e3a8a]/20 border border-[#3b82f6]/30 text-[#93c5fd] 
                         px-8 py-4 text-sm font-medium tracking-wider rounded-xl hover:bg-[#3b82f6]/20 hover:border-[#60a5fa]/40
                         transition-all duration-300 backdrop-blur-xl shadow-lg hover:shadow-xl"
                                >
                                    {/* <Play className="w-4 h-4 mr-2" /> */}
                                    <span>FOLLOW THE BUILD <i class="ml-2 bi bi-wrench"></i></span>
                                </a>
                            </div>
                        </motion.div>
                    </div>
                </main>
                {/* Additional gradient overlay for smoother transition */}
                <div
                    className="absolute left-0 right-0 bottom-0 h-32 z-0 pointer-events-none"
                    style={{
                        background:
                            "linear-gradient(to bottom, transparent, rgba(0, 6, 126, 0.3) 50%, rgba(0, 4, 86, 0.6) 100%)",
                    }}
                ></div>
                {/* Distribution Modal */}
                <DistributionModal
                    isOpen={showDistributionModal}
                    onClose={() => setShowDistributionModal(false)}
                />
                <PricingPlanModal isOpen={showPricingPlanModal} onClose={() => setShowPricingPlanModal(false)} returnUrl={window.location.origin + "/dashboard"} />
            </div>
        </>
    )
}
